import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import _ from 'lodash';
import ReactTimeout from 'react-timeout';
import Loader from 'components/Loader';
import { deepLinkFavoriteSeries } from 'helpers/AccountHelper';
import { noop } from 'helpers/GlobalHelper';
import { isStaticRender, getErrorComponent } from 'helpers/ContentHelper';
import { breadcrumbsSeriesDetailParser } from 'components/Breadcrumbs';
import { isNationEntitled, seriesAnonymousEnabled } from 'helpers/ConfigHelper';
import {
  makeSelectSeriesDetailInline,
  makeSelectSeriesDetailLatestEpisode,
  makeSelectSeriesDetailPromos,
  makeSelectSeriesDetailRecommendedPanel,
  makeSelectSeriesDetailSeo,
  makeSelectSeriesDetailSeriesData,
  makeSelectSeriesDetailTabs,
} from 'screens/ContentDetail/selectors';
import {
  clearSeriesDetail,
  fetchAnonymousCategoryDetail,
  fetchAnonymousSeries,
  fetchCategoryDetail,
  fetchSeries,
  setSeriesDetailError,
} from 'screens/ContentDetail/ContentDetailActions';
import {
  Events,
  getFavoritesListString,
  getTealiumDetailTabData,
  getTealiumSourceCollectionGlobal,
  handleAnalytics,
  PageNames,
  setSegmentContentPersonality,
  setTealiumSourceCollectionGlobal,
  trackPageReferrerAnalytics,
} from '@foxcorp/delta-web-analytics';

import {
  getSelectedTabIndex,
  getTabDataUrl,
  hasPrePremiereVideo,
  isContinueWatchingComponent,
  isInlineComponent,
  NO_TAB_STATE_TRAILER,
} from 'helpers/SeriesDetailHelpers';

import {
  setRecommendationIdAction as setRecommendationId,
  setFreemiumModalVisibilityAction as setFreemiumModalVisibility,
} from 'index/AppActions';
import { setFirstPage } from 'components/Panel/PanelActions';
import { fetchVideosTab } from 'screens/ContentDetail/components/VideosTab/VideosTabActions';
import { setUserBookmarks } from '../Collection/Actions';
import AnonymousView from './components/AnonymousView';
import FullDetailView from './components/FullDetailView';

const FETCH_NEW_CONTENT_INTERVAL_MS = 1000 * 60 * 30;

class ContentDetail extends Component {
  constructor(props) {
    super(props);
    const isPersonality = _.get(props, 'route.params.fetchFromCategories', false);
    const renderAsAnonymous = seriesAnonymousEnabled() && !isNationEntitled();
    this.state = {
      contextPlayerVisibility: false,
      deepLinkFavorited: false,
      isBrandingClicked: false,
      isPersonality,
      playlist: false,
      renderAsAnonymous,
      scrolling: false,
      stickyHeader: false,
      trackedAnalyticsReferrer: false,
    };

    this.deepLinkFavoriteSeries = deepLinkFavoriteSeries.bind(this);
    this.trackPageReferrerAnalytics = trackPageReferrerAnalytics.bind(this);
  }

  componentDidMount() {
    const { profileBookmarks, setUserBookmarks } = this.props;
    window.scrollTo(0, 0);
    this.fetchInitialData();
    this.trackPageView();
    setUserBookmarks({ profileBookmarks });

    if (isNationEntitled()) this.setState({ renderAsAnonymous: false });
  }

  componentDidUpdate(prevProps) {
    const {
      params: { showId: prevShowId },
      tabs: prevTabs,
      errors: prevErrors,
    } = prevProps;

    const {
      accessToken,
      deviceId,
      errors,
      clearSeriesDetail,
      fetchCategoryDetail,
      fetchSeries,
      params: { showId },
      tabs,
    } = this.props;
    const fetchNewSeries = prevShowId !== showId && prevShowId && showId;
    const newErrors = !prevErrors && errors;
    const newTabs = !prevTabs && tabs;
    const firePageView = newTabs || newErrors;
    if (fetchNewSeries) {
      const fetchFromCategories = _.get(this.props, 'route.params.fetchFromCategories');
      clearSeriesDetail();
      if (fetchFromCategories) {
        fetchCategoryDetail(showId, accessToken, deviceId);
      } else {
        fetchSeries(showId, accessToken, deviceId);
      }
    }
    if (firePageView) this.trackPageView();
  }

  componentWillUnmount() {
    const { clearSeriesDetail, disableScroll } = this.props;
    this.trackPageReferrerAnalytics({
      name: PageNames.SERIES_DETAIL,
    });
    setSegmentContentPersonality(false);
    clearSeriesDetail();
    disableScroll({ disableScroll: false, bodyOnly: false });
  }

  setFetchInterval = fetch => {
    const {
      params: { showId },
      accessToken,
      deviceId,
      setInterval,
    } = this.props;
    fetch(showId, accessToken, deviceId);
    setInterval(() => {
      const {
        accessToken: currentAccessToken,
        deviceId: currentDeviceId,
        params: { showId },
      } = this.props;
      fetch(showId, currentAccessToken, currentDeviceId);
    }, FETCH_NEW_CONTENT_INTERVAL_MS);
  };

  fetchInitialData() {
    if (isStaticRender()) return;
    const { renderAsAnonymous } = this.state;
    const {
      accessToken,
      deviceId,
      fetchAnonymousCategoryDetail,
      fetchCategoryDetail,
      fetchSeries,
      params: { showId },
    } = this.props;
    const fetchFromCategories = _.get(this.props, 'route.params.fetchFromCategories');
    if (fetchFromCategories && renderAsAnonymous) {
      fetchAnonymousCategoryDetail(showId, accessToken, deviceId);
    } else if (fetchFromCategories) {
      this.setFetchInterval(fetchCategoryDetail);
    } else {
      this.setFetchInterval(fetchSeries);
    }
  }

  stickyHeaderCallback = isSticky => {
    this.setState({ ...this.state, stickyHeader: isSticky });
  };

  setIsBrandingClicked = isBrandingClicked => {
    this.setState({ isBrandingClicked });
  };

  previewButtonClick = () => {
    const { disableScroll } = this.props;
    this.setState({ contextPlayerVisibility: true }, () =>
      disableScroll({ disableScroll: true, bodyOnly: true }),
    );
  };

  onCloseModal = () => {
    const { disableScroll } = this.props;
    this.setState({ contextPlayerVisibility: false }, () =>
      disableScroll({ disableScroll: false, bodyOnly: false }),
    );
  };

  trackPageView = () => {
    const {
      pageViewEvent,
      tabs,
      seriesData,
      seriesType,
      getUseUtagGlobal,
      errors,
      favoritesManager: { favorites },
    } = this.props;
    const { isPersonality } = this.state;
    if (!seriesData) return;
    const tab = tabs?.[0];
    const pageTitle = _.get(seriesData, 'name');
    const referringCollection = getTealiumSourceCollectionGlobal();
    const pageType = `detail:${seriesType}`;
    const seriesCollection = getTealiumDetailTabData({ seriesData, tab });
    const selectedMenuItem = _.get(seriesCollection, 'selectedMenuItem');

    if ((tab || errors) && pageViewEvent) {
      if (referringCollection) {
        const { name: title } = referringCollection;
        setTealiumSourceCollectionGlobal({ ...referringCollection, title });
      }

      const collection = referringCollection;

      pageViewEvent({
        pageName: PageNames.DETAIL,
        collection,
        useUtagGlobal: getUseUtagGlobal(),
        seriesType: isPersonality ? 'personality' : seriesType,
        pageTitle,
        pageType: `${pageType}:landing`,
        selectedMenuItem,
      });
      // Fire after page view event
      const hasFavorite = !!_.filter(favorites, { showCode: seriesData.showCode })[0];
      if (!hasFavorite)
        this.deepLinkFavoriteSeries({
          props: this.props,
          setDeepLinkFavorited: this.setDeepLinkFavorited,
        });
    }

    handleAnalytics(
      {
        event: Events.DETAIL_PAGE_MENU_ITEM_VIEW,
        payload: {
          pageName: `detail:${seriesType}:${pageTitle}:${selectedMenuItem}`,
          pageType: `detail:${seriesType}:menu`,
        },
      },
      {
        event: Events.DETAIL_PAGE_DEFAULT_STATE,
        payload: {
          pageName: `${pageType}:${pageTitle}`,
          pageType: `${pageType}:landing`,
          ...(favorites && {
            favoritesContentLength: favorites.length,
            favoritesContentList: getFavoritesListString(favorites),
          }),
          pageCollectionTitle: referringCollection ? referringCollection.name : null,
          pageCollectionType: referringCollection
            ? referringCollection.collectionType
            : 'no collection type',
        },
      },
    );
  };

  getFeaturedBio = (pageId, bioProfiles) => {
    const pageIdMatch = _.get(bioProfiles, `${pageId}.featured`, null);
    if (pageIdMatch) return pageIdMatch;
    return _.find(bioProfiles, profile => pageId === _.get(profile, 'featured.id'));
  };

  getBreadcrumbsData() {
    const { params, seriesData } = this.props;
    const { isPersonality } = this.state;
    const articleData = null;
    const creditsData = null;
    const breadcrumbsData = breadcrumbsSeriesDetailParser(
      params,
      seriesData,
      articleData,
      creditsData,
      isPersonality,
    );
    return breadcrumbsData;
  }

  render() {
    const {
      errors,
      hasAccountOrProvider,
      inline,
      isFreemium,
      seriesData,
      setFreemiumModalVisibility,
      setLoading,
      tabs,
    } = this.props;
    const { contextPlayerVisibility, isPersonality, renderAsAnonymous, stickyHeader } = this.state;
    const showId = _.get(inline, '@id', '');
    const paramShowId = _.get(this.props, 'params.showId', '');
    const isSameShowAsTheUrlParam = showId.indexOf(paramShowId) !== -1;
    const showLoader = (!isPersonality && !isSameShowAsTheUrlParam) || (!inline && !tabs);
    const breadcrumbsData = this.getBreadcrumbsData();
    if (errors) return getErrorComponent(errors);

    if (!seriesData || showLoader) return <Loader setLoading={setLoading} center />;
    /**
     * isFreemium is always coming as false
     */
    return renderAsAnonymous || (isFreemium && !hasAccountOrProvider) ? (
      <AnonymousView
        contextPlayerVisibility={contextPlayerVisibility}
        isPersonality={isPersonality}
        stickyHeader={stickyHeader}
        previewButtonClick={this.previewButtonClick}
        onCloseModal={this.onCloseModal}
        setIsBrandingClicked={this.setIsBrandingClicked}
        stickyHeaderCallback={this.stickyHeaderCallback}
        breadcrumbsData={breadcrumbsData}
        isFreemium={isFreemium}
        setFreemiumModalVisibility={setFreemiumModalVisibility}
        {...this.props}
      />
    ) : (
      <FullDetailView {...this.props} breadcrumbsData={breadcrumbsData} />
    );
  }
}

ContentDetail.contextTypes = {
  router: PropTypes.shape({}),
};

ContentDetail.defaultProps = {
  accessToken: null,
  account: null,
  adblock: false,
  anonymousId: null,
  articlePanels: null,
  authCheckComplete: false,
  authSuccess: false,
  bioProfiles: null,
  continueWatching: null,
  deviceId: null,
  disableScroll: noop,
  errors: null,
  favoritableItems: [],
  favoritesManager: {},
  inline: {},
  isAuthenticated: false,
  isHeader: false,
  isLatestEpisodeLive: null,
  latestEpisode: null,
  openSignIn: noop,
  openSignUp: noop,
  overlay: null,
  preauthorizedResources: [],
  profileId: null,
  promos: null,
  provider: null,
  recommendationIdData: null,
  renderAuthContainer: noop,
  route: {},
  seo: null,
  seriesData: null,
  seriesType: null,
  setAccountCloseIgnoreRedirect: noop,
  setIsAuthenticated: noop,
  setIsPlayerLoaded: noop,
  setLoading: null,
  setMVPDToAppState: noop,
  setProvider: noop,
  tabs: [],
  toggleMvpdAuthOverlay: noop,
};

ContentDetail.propTypes = {
  accessToken: PropTypes.string,
  account: PropTypes.shape({}),
  adblock: PropTypes.bool,
  anonymousId: PropTypes.string,
  anonymousLogin: PropTypes.func.isRequired,
  articlePanels: PropTypes.shape({}),
  authCheckComplete: PropTypes.bool,
  authSuccess: PropTypes.bool,
  bioProfiles: PropTypes.shape({}),
  bookmarks: PropTypes.shape({}),
  breakpoint: PropTypes.string.isRequired,
  clearSeriesDetail: PropTypes.func.isRequired,
  continueWatching: PropTypes.shape({}),
  deviceId: PropTypes.string,
  disableScroll: PropTypes.func,
  errors: PropTypes.number,
  favoritableItems: PropTypes.arrayOf(PropTypes.shape({})),
  favoritesManager: PropTypes.shape({
    favorites: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    setFavorites: PropTypes.func.isRequired,
    deleteFavorites: PropTypes.func.isRequired,
  }),
  fetchAnonymousCategoryDetail: PropTypes.func.isRequired,
  fetchAnonymousSeries: PropTypes.func.isRequired,
  fetchCategoryDetail: PropTypes.func.isRequired,
  fetchSeries: PropTypes.func.isRequired,
  getUseUtagGlobal: PropTypes.func.isRequired,
  hasAccountOrProvider: PropTypes.bool,
  inline: PropTypes.shape({}),
  isAuthenticated: PropTypes.bool,
  isFreemium: PropTypes.bool,
  isHeader: PropTypes.bool,
  isLatestEpisodeLive: PropTypes.bool,
  latestEpisode: PropTypes.shape({}),
  openSignIn: PropTypes.func,
  openSignUp: PropTypes.func,
  overlay: PropTypes.string,
  pageViewEvent: PropTypes.func.isRequired,
  params: PropTypes.shape({
    showId: PropTypes.string,
  }).isRequired,
  preauthorizedResources: PropTypes.arrayOf(PropTypes.string),
  profileBookmarks: PropTypes.shape({}),
  profileId: PropTypes.string,
  promos: PropTypes.arrayOf(PropTypes.shape({})),
  provider: PropTypes.func,
  recommendationIdData: PropTypes.oneOfType([PropTypes.node, PropTypes.shape({})]),
  removeVideoHero: PropTypes.func.isRequired,
  renderAuthContainer: PropTypes.func,
  route: PropTypes.shape({}),
  saveLiveVideo: PropTypes.func,
  seo: PropTypes.shape({}),
  seriesData: PropTypes.shape({}),
  seriesType: PropTypes.string,
  setAccountCloseIgnoreRedirect: PropTypes.func,
  setFreemiumModalVisibility: PropTypes.func.isRequired,
  setInterval: PropTypes.func.isRequired,
  setIsAuthenticated: PropTypes.func,
  setIsPlayerLoaded: PropTypes.func,
  setLoading: PropTypes.func,
  setMVPDToAppState: PropTypes.func,
  setOverlay: PropTypes.func.isRequired,
  setProvider: PropTypes.func,
  setRecommendationId: PropTypes.func.isRequired,
  setUserBookmarks: PropTypes.func.isRequired,
  setVideoHero: PropTypes.func.isRequired,
  tabs: PropTypes.arrayOf(PropTypes.shape({})),
  toggleMvpdAuthOverlay: PropTypes.func,
};

function mapStateToProps(state) {
  return {
    bookmarks: state.collection.bookmarks,
    continueWatchingItems: state.seriesDetail.continueWatchingItems,
    errors: state.seriesDetail.errors,
    favoritableItems: state.seriesDetail.favoritableItems,
    inline: makeSelectSeriesDetailInline(state),
    latestEpisode: makeSelectSeriesDetailLatestEpisode(state),
    profileBookmarks: state.app.profileBookmarks,
    promos: makeSelectSeriesDetailPromos(state),
    recommendationIdData: state.app.recommendationIdData,
    recommended: makeSelectSeriesDetailRecommendedPanel(state),
    seo: makeSelectSeriesDetailSeo(state),
    seriesData: makeSelectSeriesDetailSeriesData(state),
    seriesType: state.seriesDetail.seriesType,
    tabs: makeSelectSeriesDetailTabs(state),
  };
}

ContentDetail.fetchData = ({ store, params, location, accessToken }) => {
  const { pathname } = location;
  const { showId, pageType, pageId } = params;
  const pathNameParts = pathname.split('/');
  if (pathNameParts[1] === 'personalities') {
    return store.dispatch(fetchCategoryDetail(showId, accessToken));
  }

  return store
    .dispatch(dispatch => dispatch(fetchSeries(showId, accessToken)))
    .then(async response => {
      const panels = _.get(response, 'payload.data.panels.member', null);
      if (!panels) return null;
      const tabComponents = panels.filter(
        elem => !isInlineComponent(elem) && !isContinueWatchingComponent(elem),
      );
      const tabItems = _.get(tabComponents, '0.items.member', null);
      if (!tabItems) return null;

      const inlineComponent = panels.find(isInlineComponent);
      const hasTrailer = hasPrePremiereVideo(inlineComponent);
      const trailer = _.get(inlineComponent, 'items.member.0.previewVideo.member.0', false);
      const noTabHomepage = hasTrailer && trailer && trailer.name ? NO_TAB_STATE_TRAILER : null;
      const calculatedPageId = pageType && pageId ? `${pageType}/${pageId}` : pageId || pageType;
      const noTabState = !calculatedPageId && noTabHomepage ? noTabHomepage : null;
      const seriesType = _.get(inlineComponent, 'items.member.0.seriesType', null);
      const { tabIndex } = getSelectedTabIndex({
        pageId: calculatedPageId,
        tabs: tabItems,
        noTabState,
        noTabHomepage,
        seriesType,
      });
      const selectedTab = tabItems[tabIndex];
      if (!selectedTab) return null;

      const url = getTabDataUrl(selectedTab);
      const tabId = selectedTab['@id'];
      if (url) {
        return store.dispatch(fetchVideosTab({ url, accessToken })).then(response => {
          const responseData = _.get(response, 'payload.data', null);
          if (responseData) store.dispatch(setFirstPage(responseData, tabId));
        });
      }
      return store.dispatch(setFirstPage(selectedTab, tabId));
    });
};

const TimeoutContent = ReactTimeout(
  connect(mapStateToProps, {
    clearSeriesDetail,
    fetchAnonymousCategoryDetail,
    fetchAnonymousSeries,
    fetchCategoryDetail,
    fetchSeries,
    setFreemiumModalVisibility,
    setRecommendationId,
    setSeriesDetailError,
    setUserBookmarks,
  })(ContentDetail),
);

export default TimeoutContent;
